import { z } from 'zod'
import { optionalText, pageQuerySchema, versionSchema } from './common.js'
import { isOmanWilayatOf, omanGovernorate } from './oman-places.js'

/**
 * The organisation structure (ADR-025, docs/refactor/recruitment/01-platform-prerequisites.md §3): one
 * tree of org units per company, each with a head, an optional acting head for a period and an HR
 * business partner, plus the work locations units default to. Reads are open to every signed-in
 * user (pickers need the tree); changes need `orgUnits.manage`. Units are archived, never deleted.
 */

export const ORG_UNIT_KINDS = [
  'MINISTRY',
  'DG',
  'DEPARTMENT',
  'SECTION',
  'DIVISION',
  'BRANCH',
  'OTHER',
] as const
export const orgUnitKindSchema = z.enum(ORG_UNIT_KINDS)
export type OrgUnitKind = z.infer<typeof orgUnitKindSchema>

export const ORG_UNIT_KIND_LABELS: Record<OrgUnitKind, { en: string; ar: string }> = {
  MINISTRY: { en: 'Ministry', ar: 'وزارة' },
  DG: { en: 'Directorate General', ar: 'مديرية عامة' },
  DEPARTMENT: { en: 'Department', ar: 'دائرة' },
  SECTION: { en: 'Section', ar: 'قسم' },
  DIVISION: { en: 'Division', ar: 'شعبة' },
  BRANCH: { en: 'Branch', ar: 'فرع' },
  OTHER: { en: 'Other', ar: 'أخرى' },
}

export const ORG_STATUSES = ['ACTIVE', 'ARCHIVED'] as const
export type OrgStatus = (typeof ORG_STATUSES)[number]

/** Unit and work-location codes: short, stable and case-insensitively unique per company. */
export const orgCodeSchema = z
  .string()
  .trim()
  .min(1, 'Code is required')
  .max(30)
  .regex(/^[A-Za-z0-9_.-]+$/, 'Use letters, digits, dots, dashes or underscores')

const optionalId = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const optionalDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)

const orgUnitFields = z.object({
  code: orgCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(200),
  nameAr: optionalText(200),
  kind: orgUnitKindSchema.default('OTHER'),
  parentId: optionalId,
  headUserId: optionalId,
  actingHeadUserId: optionalId,
  actingFrom: optionalDate,
  /** Null = until further notice. */
  actingTo: optionalDate,
  hrbpUserId: optionalId,
  defaultWorkLocationId: optionalId,
  costCentreRef: optionalText(50),
})

type ActingFields = {
  actingHeadUserId: string | null
  actingFrom: string | null
  actingTo: string | null
}

function actingRules(v: ActingFields, ctx: z.RefinementCtx) {
  if (!v.actingHeadUserId && (v.actingFrom || v.actingTo)) {
    ctx.addIssue({ code: 'custom', path: ['actingHeadUserId'], message: 'Choose the acting head' })
  }
  if (v.actingHeadUserId && !v.actingFrom) {
    ctx.addIssue({ code: 'custom', path: ['actingFrom'], message: 'Enter when the acting period starts' })
  }
  if (v.actingFrom && v.actingTo && v.actingTo < v.actingFrom) {
    ctx.addIssue({ code: 'custom', path: ['actingTo'], message: 'The end cannot be before the start' })
  }
}

export const orgUnitInputSchema = orgUnitFields.superRefine(actingRules)
export type OrgUnitInput = z.input<typeof orgUnitInputSchema>

/** An edit keeps the code (immutable) and the parent (a move is its own command). */
export const orgUnitUpdateSchema = orgUnitFields
  .omit({ code: true, parentId: true })
  .extend({ version: versionSchema })
  .superRefine(actingRules)
export type OrgUnitUpdate = z.input<typeof orgUnitUpdateSchema>

/** Re-parents the unit with its whole subtree; null makes it a root. */
export const orgUnitMoveSchema = z.object({
  parentId: z.uuid().nullable(),
  version: versionSchema,
})
export type OrgUnitMove = z.input<typeof orgUnitMoveSchema>

export const orgUnitListQuerySchema = pageQuerySchema.extend({
  view: z.enum(['active', 'archived', 'all']).default('active'),
  kind: orgUnitKindSchema.optional(),
  /** Direct children of this unit only. */
  parentId: z.uuid().optional(),
})
export type OrgUnitListQuery = z.input<typeof orgUnitListQuerySchema>

export const orgUnitTreeQuerySchema = z.object({
  /** Archived units too (needs `orgUnits.manage`). */
  includeArchived: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
})
export type OrgUnitTreeQuery = z.input<typeof orgUnitTreeQuerySchema>

export interface OrgUserRef {
  id: string
  name: string
  nameAr: string | null
}

/** How other records show a unit. */
export interface OrgUnitRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

export interface WorkLocationRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

export interface OrgUnit extends OrgUnitRef {
  kind: OrgUnitKind
  parent: OrgUnitRef | null
  head: OrgUserRef | null
  actingHead: OrgUserRef | null
  actingFrom: string | null
  actingTo: string | null
  hrbp: OrgUserRef | null
  defaultWorkLocation: WorkLocationRef | null
  costCentreRef: string | null
  status: OrgStatus
  archivedAt: string | null
  /** Root first: the unit's ancestors (not itself). */
  path: OrgUnitRef[]
  childCount: number
  memberCount: number
  version: number
}

/** One unit of the flat tree listing, in path order (each parent before its children). */
export interface OrgUnitTreeNode extends OrgUnitRef {
  kind: OrgUnitKind
  parentId: string | null
  /** 0 for a root. */
  depth: number
  status: OrgStatus
  head: OrgUserRef | null
}

// Work locations ---------------------------------------------------------------------------------

/** Latitude/longitude as decimal strings with up to 6 places. */
const coordinate = z
  .string()
  .trim()
  .regex(/^-?\d{1,3}(\.\d{1,6})?$/, 'Invalid coordinate')
  .nullish()
  .transform((v) => (v ? v : null))

const workLocationFields = z.object({
  code: orgCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(200),
  nameAr: optionalText(200),
  governorate: optionalText(10),
  wilayat: optionalText(40),
  address: optionalText(500),
  latitude: coordinate,
  longitude: coordinate,
})

type PlaceFields = {
  governorate: string | null
  wilayat: string | null
  latitude: string | null
  longitude: string | null
}

/** Governorate, wilayat and coordinates checked together (work locations, service sites). */
export function omanPlaceRules(v: PlaceFields, ctx: z.RefinementCtx) {
  if (v.governorate && !omanGovernorate(v.governorate)) {
    ctx.addIssue({ code: 'custom', path: ['governorate'], message: 'Choose a governorate from the list' })
  }
  if (v.wilayat && !v.governorate) {
    ctx.addIssue({ code: 'custom', path: ['governorate'], message: 'Choose the governorate first' })
  } else if (v.wilayat && v.governorate && !isOmanWilayatOf(v.governorate, v.wilayat)) {
    ctx.addIssue({ code: 'custom', path: ['wilayat'], message: 'This wilayat is not in the governorate' })
  }
  if ((v.latitude === null) !== (v.longitude === null)) {
    ctx.addIssue({
      code: 'custom',
      path: [v.latitude === null ? 'latitude' : 'longitude'],
      message: 'Enter both latitude and longitude, or neither',
    })
  }
  if (v.latitude !== null && Math.abs(Number(v.latitude)) > 90) {
    ctx.addIssue({ code: 'custom', path: ['latitude'], message: 'Latitude is between -90 and 90' })
  }
  if (v.longitude !== null && Math.abs(Number(v.longitude)) > 180) {
    ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Longitude is between -180 and 180' })
  }
}

export const workLocationInputSchema = workLocationFields.superRefine(omanPlaceRules)
export type WorkLocationInput = z.input<typeof workLocationInputSchema>

export const workLocationUpdateSchema = workLocationFields
  .omit({ code: true })
  .extend({ version: versionSchema })
  .superRefine(omanPlaceRules)
export type WorkLocationUpdate = z.input<typeof workLocationUpdateSchema>

export const workLocationListQuerySchema = pageQuerySchema.extend({
  view: z.enum(['active', 'archived', 'all']).default('active'),
  governorate: z.string().trim().max(10).optional(),
})
export type WorkLocationListQuery = z.input<typeof workLocationListQuerySchema>

export interface WorkLocation extends WorkLocationRef {
  governorate: string | null
  wilayat: string | null
  address: string | null
  latitude: string | null
  longitude: string | null
  status: OrgStatus
  archivedAt: string | null
  version: number
}

export const ORG_UNIT_ERROR_CODES = [
  'ORG_UNIT_CYCLE',
  'ORG_UNIT_HAS_CHILDREN',
  'ORG_UNIT_HAS_MEMBERS',
  'ORG_UNIT_HAS_SCOPED_GRANTS',
  'ORG_UNIT_PARENT_ARCHIVED',
  'ORG_UNIT_ARCHIVED',
  'ORG_UNIT_NOT_FOUND',
  'ORG_UNIT_OUT_OF_SCOPE',
  'ORG_UNIT_REQUIRED',
  'WORK_LOCATION_IN_USE',
  'WORK_LOCATION_ARCHIVED',
  'IMPORT_STALE',
] as const
export type OrgUnitErrorCode = (typeof ORG_UNIT_ERROR_CODES)[number]
